import { encodeAudioFrame, MAX_SEGMENT_MS } from '@hub/contracts'
import type { AudioSegment } from './meeting-client'
import { downsampleToPcm16, VadSegmenter } from './segmenter'
import { MIC_WORKLET_NAME, micWorkletUrl } from './worklet'

/**
 * The browser half of live transcription (PLAN-085 §1.6): this tab's **own**
 * microphone → 16 kHz PCM16 → voice-activity segments → binary frames on the
 * room socket.
 *
 * One browser is one speaker, which is what makes speaker attribution free:
 * whatever this tab sends is attributed to the person logged into it. Nothing
 * is sent while the microphone is muted.
 */
export class MicTranscriber {
  #context: AudioContext | null = null
  #node: AudioWorkletNode | null = null
  #source: MediaStreamAudioSourceNode | null = null
  #segmenter = new VadSegmenter()
  #enabled = true
  #stopped = false

  /**
   * `onSegment` gets PCM and a duration — no sequence number and no position.
   * The socket assigns the sequence (it must be continuous across the
   * transcriber restarts a mute/unmute causes, REVIEW-1 #5) and the room
   * assigns the time (this clock is per transcriber, not per meeting,
   * REVIEW-2 #1).
   */
  constructor(private readonly onSegment: (segment: AudioSegment) => void) {}

  /** Start capturing from a live microphone track. */
  async start(track: MediaStreamTrack): Promise<void> {
    if (this.#stopped || this.#context) return
    const context = new AudioContext()
    this.#context = context
    await context.audioWorklet.addModule(micWorkletUrl())
    if (this.#stopped) return

    const node = new AudioWorkletNode(context, MIC_WORKLET_NAME)
    this.#node = node
    node.port.onmessage = (event: MessageEvent<Float32Array>) => this.#onFrame(event.data, context)

    const source = context.createMediaStreamSource(new MediaStream([track]))
    this.#source = source
    source.connect(node)
    // The worklet produces no output; connecting it to the destination would
    // play the speaker's own voice back at them.
  }

  /** Mute: the segmenter is flushed so a half-spoken sentence still lands. */
  setEnabled(enabled: boolean): void {
    if (this.#enabled === enabled) return
    this.#enabled = enabled
    this.#node?.port.postMessage({ enabled })
    if (!enabled) this.#emit(this.#segmenter.flush())
  }

  async stop(): Promise<void> {
    this.#stopped = true
    this.#emit(this.#segmenter.flush())
    this.#node?.port.close()
    this.#source?.disconnect()
    this.#node?.disconnect()
    await this.#context?.close().catch(() => {})
    this.#context = null
    this.#node = null
    this.#source = null
  }

  #onFrame(frame: Float32Array, context: AudioContext): void {
    if (!this.#enabled || this.#stopped) return
    this.#emit(this.#segmenter.push(downsampleToPcm16(frame, context.sampleRate)))
  }

  #emit(segment: { startMs: number; endMs: number; pcm: Int16Array } | null): void {
    if (!segment) return
    // The segmenter respects the cap, but the codec refuses anything longer
    // and a dropped frame is worse than a truncated one.
    const durationMs = Math.min(segment.endMs - segment.startMs, MAX_SEGMENT_MS)
    const out: AudioSegment = { durationMs, pcm: segment.pcm }
    try {
      // Encode here so a malformed segment is caught next to its cause rather
      // than inside the socket client. The seq is a placeholder — the socket
      // assigns the real one.
      encodeAudioFrame({ seq: 1, startMs: 0, endMs: durationMs, pcm: segment.pcm })
    } catch (err) {
      console.warn('[meet] dropped an unencodable segment', err)
      return
    }
    this.onSegment(out)
  }
}
